export interface DateRange {
  startDate: string;
  endDate: string;
}

/** Months of the fiscal year, in order (April → March). */
export const FISCAL_MONTHS = [
  { value: '04', label: 'Avril' }, { value: '05', label: 'Mai' },
  { value: '06', label: 'Juin' },  { value: '07', label: 'Juillet' },
  { value: '08', label: 'Août' },  { value: '09', label: 'Septembre' },
  { value: '10', label: 'Octobre' },{ value: '11', label: 'Novembre' },
  { value: '12', label: 'Décembre' },{ value: '01', label: 'Janvier' },
  { value: '02', label: 'Février' }, { value: '03', label: 'Mars' }
];

const fiscalMonthIndex = (month: string) => FISCAL_MONTHS.findIndex(m => m.value === month);
const monthLabel = (month: string) => FISCAL_MONTHS[fiscalMonthIndex(month)].label;

/** Calendar year of a month within a fiscal year (January–March fall in the following year). */
export function getMonthYear(month: string, fiscalYear: number): number {
  return parseInt(month) <= 3 ? fiscalYear + 1 : fiscalYear;
}

/** Reads the selectedPeriod of a 'month' period: one month ('09') or several ('01,03,05'). */
export function parseMonths(selectedPeriod: string): string[] {
  return selectedPeriod.split(',').map(m => m.padStart(2, '0'));
}

/** Builds the selectedPeriod of a 'month' period, months in fiscal order. */
export function formatMonths(months: string[]): string {
  return [...months].sort((a, b) => fiscalMonthIndex(a) - fiscalMonthIndex(b)).join(',');
}

/** Number of months covered by the selectedPeriod of a 'month' period. */
export function getMonthCount(selectedPeriod: string): number {
  return parseMonths(selectedPeriod).length;
}

/** Date ranges of a 'month' period, consecutive months being merged. */
export function getMonthRanges(selectedPeriod: string, fiscalYear: number): DateRange[] {
  const ranges: DateRange[] = [];
  let previousIndex = -2;
  parseMonths(selectedPeriod).forEach(month => {
    const year = getMonthYear(month, fiscalYear);
    const startDate = `${year}-${month}-01`;
    const endDate = `${year}-${month}-${new Date(year, parseInt(month), 0).getDate()}`;
    const index = fiscalMonthIndex(month);
    if (index === previousIndex + 1) {
      ranges[ranges.length - 1].endDate = endDate;
    } else {
      ranges.push({ startDate, endDate });
    }
    previousIndex = index;
  });
  return ranges;
}

/** Overall start and end dates of a 'month' period: skipped months in between are included. */
export function getMonthRangeDates(selectedPeriod: string, fiscalYear: number): DateRange {
  const ranges = getMonthRanges(selectedPeriod, fiscalYear);
  return { startDate: ranges[0].startDate, endDate: ranges[ranges.length - 1].endDate };
}

/** Date ranges of a period: only a 'month' period can have several. */
export function getPeriodRanges(
  period: 'year' | 'quarter' | 'month',
  selectedPeriod: string,
  fiscalYear: number,
  startDate: string,
  endDate: string,
): DateRange[] {
  return period === 'month' ? getMonthRanges(selectedPeriod, fiscalYear) : [{ startDate, endDate }];
}

/** Number of calendar months covered by date ranges. */
export function countRangeMonths(ranges: DateRange[]): number {
  return ranges.reduce((total, { startDate, endDate }) => {
    const [startYear, startMonth] = startDate.split('-').map(Number);
    const [endYear, endMonth] = endDate.split('-').map(Number);
    return total + (endYear - startYear) * 12 + endMonth - startMonth + 1;
  }, 0);
}

/** True if a 'YYYY-MM-DD…' date falls in one of the ranges. */
export function isDateInRanges(date: string, ranges: DateRange[]): boolean {
  const day = date.slice(0, 10);
  return ranges.some(({ startDate, endDate }) => startDate <= day && day <= endDate);
}

/** PostgREST filter matching a date column against ranges, to pass to .or() or nest in or(...). */
export function dateRangesFilter(column: string, ranges: DateRange[]): string {
  return ranges.map(r => `and(${column}.gte.${r.startDate},${column}.lte.${r.endDate})`).join(',');
}

/** Display label of a 'month' period, e.g. "Septembre 2026" or "Novembre 2026, Janvier, Mars 2027". */
export function getMonthsLabel(selectedPeriod: string, fiscalYear: number): string {
  const labelsByYear = new Map<number, string[]>();
  parseMonths(selectedPeriod).forEach(month => {
    const year = getMonthYear(month, fiscalYear);
    labelsByYear.set(year, [...(labelsByYear.get(year) ?? []), monthLabel(month)]);
  });
  return Array.from(labelsByYear, ([year, labels]) => `${labels.join(', ')} ${year}`).join(', ');
}

/** Word following "Objectif" for a 'month' period. */
export function getMonthObjectiveLabel(selectedPeriod: string): string {
  return getMonthCount(selectedPeriod) > 1 ? 'Période' : 'Mensuel';
}

/**
 * Returns true if the selected period's end date is today or in the future.
 * Potential sales only make sense for current/future periods.
 */
export function isPotentialVisible(endDateStr: string): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const periodEnd = new Date(endDateStr);
  periodEnd.setHours(0, 0, 0, 0);
  return periodEnd >= today;
}

/** Computes the end date string from period params (used by Négociateur & Direction tables). */
export function getPeriodEndDate(
  period: 'year' | 'quarter' | 'month',
  selectedPeriod: string,
  fiscalYear: number,
): string {
  if (period === 'year') {
    return `${fiscalYear + 1}-03-31`;
  }
  if (period === 'quarter') {
    const quarterEndMonth: Record<string, { end: string; year: number }> = {
      '1': { end: '06', year: fiscalYear },
      '2': { end: '09', year: fiscalYear },
      '3': { end: '12', year: fiscalYear },
      '4': { end: '03', year: fiscalYear + 1 },
    };
    const q = quarterEndMonth[selectedPeriod] ?? quarterEndMonth['1'];
    const lastDay = new Date(q.year, parseInt(q.end), 0).getDate();
    return `${q.year}-${q.end}-${String(lastDay).padStart(2, '0')}`;
  }
  // month
  return getMonthRangeDates(selectedPeriod, fiscalYear).endDate;
}
